import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Image as ImageIcon,
  Biotech as BiotechIcon,
  Analytics as AnalyticsIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  ModelTraining as ModelTrainingIcon,
  IntegrationInstructions as IntegrationIcon
} from "@mui/icons-material";
import reportData from "./aiBreastCancerReport.json";

const AIBreastCancerReport: React.FC = () => {
  const aiReport = reportData.ai_breast_cancer_report;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: "100%", margin: "0 auto" }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }} variant="outlined">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            AI Breast Cancer Analysis Report
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {aiReport.metadata.ai_system.name} v{aiReport.metadata.ai_system.version}
          </Typography>
          <Chip
            label={`Report ID: ${aiReport.metadata.report_id}`}
            variant="outlined"
            sx={{ mt: 1 }}
          />
          <Chip
            label={aiReport.metadata.ai_system.fda_clearance}
            color="success"
            variant="outlined"
            sx={{ mt: 1, ml: 1 }}
            size="small"
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Generated: {new Date(aiReport.metadata.generation_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Data Sources: {aiReport.metadata.data_sources.join(", ")}
          </Typography>
        </Box>
      </Paper>

      {/* Patient Information */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Patient Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.deidentified_id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Age</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.demographics.age}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.demographics.gender}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Menopause Status</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.demographics.menopause_status}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">BRCA Status</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.clinical_background.family_history.brca_status}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">First Degree Relatives with BC</Typography>
              <Typography variant="body1" gutterBottom>{aiReport.patient.clinical_background.family_history.first_degree_relatives_with_bc}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Imaging Analysis */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ImageIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Imaging Analysis</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Mammography */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Mammography</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2"><strong>Laterality:</strong> {aiReport.imaging_analysis.mammography.laterality}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2"><strong>View:</strong> {aiReport.imaging_analysis.mammography.view}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2"><strong>BIRADS:</strong> 
                      <Chip 
                        label={aiReport.imaging_analysis.mammography.ai_findings.birads_score.ai_suggestion} 
                        color="warning" 
                        size="small" 
                        sx={{ ml: 1 }} 
                      />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2"><strong>Confidence:</strong> 
                      <Chip 
                        label={`${(aiReport.imaging_analysis.mammography.ai_findings.suspicious_mass.confidence * 100).toFixed(0)}%`} 
                        color="success" 
                        size="small" 
                        sx={{ ml: 1 }} 
                      />
                    </Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" fontWeight="bold">Suspicious Mass</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>Size:</strong> {aiReport.imaging_analysis.mammography.ai_findings.suspicious_mass.size_mm.join(' × ')} mm</Typography>
                      <Typography variant="body2"><strong>Morphology:</strong> {aiReport.imaging_analysis.mammography.ai_findings.suspicious_mass.morphology}</Typography>
                      <Typography variant="body2"><strong>Margin:</strong> {aiReport.imaging_analysis.mammography.ai_findings.suspicious_mass.margin_analysis}</Typography>
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" fontWeight="bold">Microcalcifications</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>Present:</strong> 
                        <Chip 
                          label={aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.present ? 'Yes' : 'No'} 
                          color={aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.present ? 'primary' : 'default'} 
                          size="small" 
                          sx={{ ml: 1 }} 
                        />
                      </Typography>
                      {aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.present && (
                        <>
                          <Typography variant="body2"><strong>Distribution:</strong> {aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.distribution}</Typography>
                          <Typography variant="body2"><strong>Morphology:</strong> {aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.morphology}</Typography>
                          <Typography variant="body2"><strong>Suspicion Score:</strong> {(aiReport.imaging_analysis.mammography.ai_findings.microcalcifications.suspicion_score * 100).toFixed(0)}%</Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Ultrasound */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Ultrasound</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Elasticity Score:</strong> {aiReport.imaging_analysis.ultrasound.ai_analysis.elasticity_score}</Typography>
                <Typography variant="body2"><strong>Vascularity:</strong> {aiReport.imaging_analysis.ultrasound.ai_analysis.doppler_analysis.vascularity}</Typography>
                <Typography variant="body2"><strong>RI Value:</strong> {aiReport.imaging_analysis.ultrasound.ai_analysis.doppler_analysis.ri_value}</Typography>
                <Typography variant="body2"><strong>Malignancy Probability:</strong> 
                  <Chip 
                    label={`${(aiReport.imaging_analysis.ultrasound.ai_analysis.malignancy_probability * 100).toFixed(0)}%`} 
                    color={aiReport.imaging_analysis.ultrasound.ai_analysis.malignancy_probability > 0.7 ? 'error' : 'warning'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Typography>
              </Paper>
            </Grid>

            {/* MRI */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">MRI</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Initial Enhancement:</strong> {aiReport.imaging_analysis.mri.kinetic_analysis.initial_enhancement}</Typography>
                <Typography variant="body2"><strong>Delayed Washout:</strong> {aiReport.imaging_analysis.mri.kinetic_analysis.delayed_washout}</Typography>
                <Typography variant="body2"><strong>Curve Type:</strong> {aiReport.imaging_analysis.mri.kinetic_analysis.curve_type}</Typography>
                <Typography variant="body2"><strong>Tumor Volume:</strong> {aiReport.imaging_analysis.mri.tumor_volume_3d}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Pathology Analysis */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <BiotechIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Pathology Analysis</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Whole Slide Image Analysis */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Whole Slide Image Analysis</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Total Tumor Area:</strong> {aiReport.pathology_analysis.whole_slide_image.tumor_detection.total_tumor_area} mm²</Typography>
                <Typography variant="body2"><strong>Tumor Cellularity:</strong> {aiReport.pathology_analysis.whole_slide_image.tumor_detection.tumor_cellularity}%</Typography>
                <Typography variant="body2"><strong>Tumor Stroma Ratio:</strong> {aiReport.pathology_analysis.whole_slide_image.tumor_detection.tumor_stroma_ratio}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" fontWeight="bold">Nuclear Grade</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2"><strong>Pleomorphism Score:</strong> {aiReport.pathology_analysis.whole_slide_image.nuclear_grade.pleomorphism_score}</Typography>
                  <Typography variant="body2"><strong>Mitotic Count:</strong> {aiReport.pathology_analysis.whole_slide_image.nuclear_grade.mitotic_count}</Typography>
                  <Typography variant="body2"><strong>Tubule Formation:</strong> {aiReport.pathology_analysis.whole_slide_image.nuclear_grade.tubule_formation}</Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" fontWeight="bold">TILs Analysis</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2"><strong>Stromal TILs:</strong> {aiReport.pathology_analysis.whole_slide_image.tils_analysis.stromal_tils_percentage}%</Typography>
                  <Typography variant="body2"><strong>Spatial Distribution:</strong> {aiReport.pathology_analysis.whole_slide_image.tils_analysis.spatial_distribution}</Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Immunohistochemistry */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Immunohistochemistry</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>ER Status:</strong>
                  <Chip
                    label={aiReport.pathology_analysis.immunohistochemistry.er_status.prediction}
                    color={aiReport.pathology_analysis.immunohistochemistry.er_status.prediction === 'Positive' ? 'primary' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                  <Chip
                    label={`${(aiReport.pathology_analysis.immunohistochemistry.er_status.confidence * 100).toFixed(0)}%`}
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2"><strong>H Score:</strong> {aiReport.pathology_analysis.immunohistochemistry.er_status.h_score}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>HER2 FISH Prediction:</strong>
                  <Chip
                    label={aiReport.pathology_analysis.immunohistochemistry.her2_equivocal_analysis.fish_prediction}
                    color={aiReport.pathology_analysis.immunohistochemistry.her2_equivocal_analysis.fish_prediction === 'Positive' ? 'primary' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                  <Chip
                    label={`${(aiReport.pathology_analysis.immunohistochemistry.her2_equivocal_analysis.confidence * 100).toFixed(0)}%`}
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Prognostic Analysis */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Prognostic Analysis</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Recurrence Risk */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Recurrence Risk</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2"><strong>5-Year Risk:</strong></Typography>
                  <Chip 
                    label={`${(aiReport.prognostic_analysis.recurrence_risk.five_year_risk * 100).toFixed(0)}%`} 
                    color={aiReport.prognostic_analysis.recurrence_risk.five_year_risk > 0.2 ? 'error' : 'warning'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2"><strong>10-Year Risk:</strong></Typography>
                  <Chip 
                    label={`${(aiReport.prognostic_analysis.recurrence_risk.ten_year_risk * 100).toFixed(0)}%`} 
                    color={aiReport.prognostic_analysis.recurrence_risk.ten_year_risk > 0.25 ? 'error' : 'warning'} 
                    size="small" 
                  />
                </Box>
                
                <Typography variant="body2" fontWeight="bold">Significant Factors:</Typography>
                <List dense>
                  {aiReport.prognostic_analysis.recurrence_risk.significant_factors.map((factor, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <AssignmentIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={factor} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Treatment Response */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Treatment Response</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>pCR Probability:</strong>
                  <Chip 
                    label={`${(aiReport.prognostic_analysis.treatment_response.neoadjuvant_response_prediction.pcr_probability * 100).toFixed(0)}%`} 
                    color={aiReport.prognostic_analysis.treatment_response.neoadjuvant_response_prediction.pcr_probability > 0.3 ? 'success' : 'warning'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Typography>
                <Typography variant="body2"><strong>Recommended Regimen:</strong> {aiReport.prognostic_analysis.treatment_response.neoadjuvant_response_prediction.recommended_regimen}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>Endocrine Sensitivity Score:</strong>
                  <Chip 
                    label={`${(aiReport.prognostic_analysis.treatment_response.endocrine_sensitivity.ai_score * 100).toFixed(0)}%`} 
                    color={aiReport.prognostic_analysis.treatment_response.endocrine_sensitivity.ai_score > 0.7 ? 'success' : 'warning'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Typography>
                <Typography variant="body2"><strong>Predicted Benefit:</strong> 
                  <Chip 
                    label={aiReport.prognostic_analysis.treatment_response.endocrine_sensitivity.predicted_benefit} 
                    color={aiReport.prognostic_analysis.treatment_response.endocrine_sensitivity.predicted_benefit === 'High' ? 'success' : 'default'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Decision Support */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ScienceIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Decision Support</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* NCCN Guidelines */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">NCCN Guideline Alignment</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2" fontWeight="bold">Recommended Workup:</Typography>
                <List dense>
                  {aiReport.decision_support.nccn_guideline_alignment.recommended_workup.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <AssignmentIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
                
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>Treatment Options:</Typography>
                {aiReport.decision_support.nccn_guideline_alignment.treatment_options.map((option, index) => (
                  <Box key={index} sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">{option.option}</Typography>
                      <Chip 
                        label={`${(option.confidence * 100).toFixed(0)}% confidence`} 
                        color={option.confidence > 0.7 ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </Box>
                    
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="success.main">Pros:</Typography>
                        <List dense>
                          {option.pros.map((pro, i) => (
                            <ListItem key={i} sx={{ py: 0 }}>
                              <ListItemText primary={pro} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="error.main">Cons:</Typography>
                        <List dense>
                          {option.cons.map((con, i) => (
                            <ListItem key={i} sx={{ py: 0 }}>
                              <ListItemText primary={con} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* Clinical Trial Matching */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Clinical Trial Matching</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                {aiReport.decision_support.clinical_trial_matching.matches.map((match, index) => (
                  <Box key={index} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">{match.title}</Typography>
                      <Chip 
                        label={`${(match.match_score * 100).toFixed(0)}% match`} 
                        color={match.match_score > 0.8 ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </Box>
                    <Typography variant="body2"><strong>Trial ID:</strong> {match.trial_id}</Typography>
                    <Typography variant="body2"><strong>Eligibility:</strong> {match.eligibility_criteria}</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Explainability & Integration */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ModelTrainingIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">AI Model Details</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Confidence Metrics */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Confidence Metrics</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2"><strong>Imaging Confidence:</strong></Typography>
                  <Chip 
                    label={`${(aiReport.explanability.confidence_metrics.imaging_confidence * 100).toFixed(0)}%`} 
                    color={aiReport.explanability.confidence_metrics.imaging_confidence > 0.85 ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2"><strong>Pathology Confidence:</strong></Typography>
                  <Chip 
                    label={`${(aiReport.explanability.confidence_metrics.pathology_confidence * 100).toFixed(0)}%`} 
                    color={aiReport.explanability.confidence_metrics.pathology_confidence > 0.85 ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Clinical Correlation:</strong></Typography>
                  <Chip 
                    label={`${(aiReport.explanability.confidence_metrics.clinical_correlation * 100).toFixed(0)}%`} 
                    color={aiReport.explanability.confidence_metrics.clinical_correlation > 0.8 ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" fontWeight="bold">Model Limitations:</Typography>
                <List dense>
                  {aiReport.explanability.model_limitations.map((limitation, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <AssignmentIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={limitation} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Integration */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">System Integration</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IntegrationIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2"><strong>EMR Ready:</strong></Typography>
                  <Chip 
                    label={aiReport.integration.emr_ready ? 'Yes' : 'No'} 
                    color={aiReport.integration.emr_ready ? 'success' : 'error'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IntegrationIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2"><strong>DICOM Compatible:</strong></Typography>
                  <Chip 
                    label={aiReport.integration.dicom_compatible ? 'Yes' : 'No'} 
                    color={aiReport.integration.dicom_compatible ? 'success' : 'error'} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IntegrationIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2"><strong>HL7 Interface:</strong> {aiReport.integration.hl7_interface}</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" fontWeight="bold">Model Versions:</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary={`Imaging Analysis: ${aiReport.metadata.ai_system.model_versions.imaging_analysis}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={`Pathology Analysis: ${aiReport.metadata.ai_system.model_versions.pathology_analysis}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={`Prognostic Analysis: ${aiReport.metadata.ai_system.model_versions.prognostic_analysis}`} 
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AIBreastCancerReport;